import { formatDay, formatTime } from "../format.js";
import type { LessonBlockDetail } from "./staff.js";

export interface RegistrationSettlementWarning {
  message: string;
  /** Replaying the same registration (same key) may still settle past lessons. */
  retryable: boolean;
}

const RETRY_MESSAGE =
  "Block added, but past lessons could not yet be updated. Retry this same addition to update them; do not add another block.";
const SAME_BLOCK_MESSAGE =
  "Block added, but some past lessons were previously paid from this same block and then reversed. They need a different block or a manual payment; retrying will not change them.";
const REVIEW_MESSAGE =
  "Block added, but a past lesson's credit history needs staff review before it can be paid. Retrying will not change it.";

export function registrationSettlementWarning(
  detail: LessonBlockDetail,
): RegistrationSettlementWarning | null {
  const outcome = detail.reconciliation;
  if (!outcome || outcome.status === "completed" || outcome.status === "not_applicable") return null;
  if (outcome.same_block_reuse_skipped > 0 && outcome.status === "incomplete") {
    return { message: SAME_BLOCK_MESSAGE, retryable: false };
  }
  // A failure with an exact count is a ledger-review hold, not a transient error.
  if (outcome.status === "failed" && outcome.credits_applied !== null) {
    return { message: REVIEW_MESSAGE, retryable: false };
  }
  return { message: RETRY_MESSAGE, retryable: true };
}

export function registrationReconciliationWarning(detail: LessonBlockDetail): string | null {
  return registrationSettlementWarning(detail)?.message ?? null;
}


type SettledLesson = NonNullable<NonNullable<LessonBlockDetail["reconciliation"]>["settled_lessons"]>[number];

export interface RegistrationSettledSummary {
  registrationBlockId: string;
  settledLessons: SettledLesson[];
  heading: string;
  lessons: string[];
  more: number;
}

export function registrationSettledSummary(
  detail: LessonBlockDetail,
  previous: RegistrationSettledSummary | null = null,
): RegistrationSettledSummary | null {
  if (!detail.reconciliation) return null;
  // A replay reports only lessons paid in that call. Retain the accepted
  // registration's earlier evidence without reusing it for another block.
  const earlier = previous?.registrationBlockId === detail.block.id
    ? previous.settledLessons : [];
  const byBooking = new Map<string, SettledLesson>();
  for (const lesson of [...earlier, ...(detail.reconciliation.settled_lessons ?? [])]) {
    byBooking.set(`${lesson.amelia_appointment_id}:${lesson.amelia_booking_id}`, lesson);
  }
  const settled = [...byBooking.values()];
  if (settled.length === 0) return null;

  const visible = settled.slice(0, 5).map(
    (lesson) => `${formatDay(lesson.starts_at)}, ${formatTime(lesson.starts_at)}`,
  );
  return {
    registrationBlockId: detail.block.id,
    settledLessons: settled,
    heading: `Paid from credits: ${settled.length} past ${settled.length === 1 ? "lesson" : "lessons"} (oldest first)`,
    lessons: visible,
    more: Math.max(0, settled.length - visible.length),
  };
}
